import type CustomWindow from 'interfaces/Utils';
import type { Tinypass } from './entities/Tinypass';
import type { PianoPageConfig } from './entities/PianoPageConfig';
import pianoHandlers from './pianoHandlers';
import passwordReset from './passwordReset';
import type { DataLayerEventName, EventData } from 'services/Gtm/entities';
import type { ConversionData, LinkedTerm, ResponseVariables } from './entities/PianoCallbackParams';
import CookieService from 'utils/miscUtils/CookieService';
import { pianoTemplateTrackingEvent } from '@/main/lib/services/Piano/pianoTracking';
import { isSubscribePage } from '@/main/lib/utils/paywallUtils';
import { updateLocalStorageObject } from '@/main/lib/utils/localStorageUtils';
import type { Client } from '@optimizely/optimizely-sdk';

export * from './setPianoConfig';
export * from 'services/Subscriber';

declare let window: CustomWindow;

/**
 * Run to initalize piano
 * See: https://docs.piano.io/track/implementing-piano#implementsinglepage
 *
 * @param pianoPageConfig
 */
export async function pianoInit(
  pianoPageConfig: PianoPageConfig,
  isBypassed: boolean,
  updateResponseVariables: (responseVariables: ResponseVariables) => void,
  sendEventOnConsent: (eventData: EventData, eventType?: DataLayerEventName) => void,
  setConversionData: React.Dispatch<React.SetStateAction<ConversionData | null>>,
  optimizely: Client,
) {
  let tp = await pianoSetSiteData(updateResponseVariables, sendEventOnConsent, setConversionData, optimizely);
  tp = pianoSetPageData(tp, pianoPageConfig, isBypassed);
  tp.push([
    'init',
    () => {
      // tp methods are called within the init callback
      passwordReset(tp);
      tp.pianoId.init();
      tp.experience.init();
    },
  ]);
  window.addEventListener('message', (event) => {
    const subscribePage = isSubscribePage();
    // Generate and store custom referral params from custom event sent from Piano offer template used
    // on non-subscribe pages
    if (event.data.eventName === 'customOfferTrackingParams' && !subscribePage) {
      // NOTE: Do we neeed to check persistReferralParams or do we always want last one of these to rule?
      const itmSource = event.data.params?.itmSource;
      const encodedUrl = encodeURIComponent(window.location.href);
      const referralParams = `itm_source=${itmSource}&redirect=${encodedUrl}`;
      updateLocalStorageObject('checkoutTrackingProps', {
        checkout_referral_params: referralParams || null,
        persistReferralParams: true,
      });
    }
    if (event.data.eventName === 'pianoTemplateTrackingEvent') {
      pianoTemplateTrackingEvent(tp, event.data, sendEventOnConsent);
    }
  });
}

/**
 * Run only on history change
 * @param pianoPageConfig
 */
// export function pianoRunExperience(pianoPageConfig: PianoPageConfig) {
//   let tp = window.tp || [];
//   tp = pianoSetPageData(tp, pianoPageConfig);
//   tp.experience.execute();
// }

function onLinkedTermSelected(sessionId: string, linkedTerm: LinkedTerm) {
  const {
    linkedTermData: { externalTermId },
  } = linkedTerm;
  CookieService.set('pianoCheckoutSessionId', sessionId, { expires: 1 });
  window.location.href = `/checkout/${externalTermId}`;
}

async function getComposerCountryCode() {
  try {
    let countryCode: string;
    const cachedCountryCode = CookieService.get('composerCountryCode');
    if (cachedCountryCode && cachedCountryCode.length === 2) {
      countryCode = cachedCountryCode;
    } else {
      const response = await fetch('/api/getCountryCode');
      if (!response.ok) {
        return null;
      }
      const data: { country: string; source: string } = await response.json();
      countryCode = data.country;
      if (!countryCode || countryCode.length !== 2) {
        return null;
      }
      CookieService.set('composerCountryCode', countryCode, { expires: 1 });
    }
    return countryCode;
  } catch {
    return null;
  }
}

/**
 * Handles pushing data into @tp object prior to the loading of the
 * piano Tinypass library and the initialization. No @tp methods
 * shoudld be called here.
 */
export async function pianoSetSiteData(
  updateResponseVariables: (responseVariables: ResponseVariables) => void,
  sendEventOnConsent: (eventData: EventData, eventType?: DataLayerEventName) => void,
  setConversionData: React.Dispatch<React.SetStateAction<ConversionData | null>>,
  optimizely: Client,
) {
  const tp = window.tp || ([] as unknown as Tinypass);
  const domain = window.location.hostname;
  tp.push(['setAid', process.env.PIANO_AID]);
  tp.push(['setCxenseSiteId', process.env.PIANO_SITE_ID]);
  tp.push(['setSandbox', process.env.PIANO_SANDBOX]);
  tp.push(['setCookieDomain', domain]);
  tp.push(['setUsePianoIdUserProvider', true]);
  tp.push(['setUseTinypassAccounts', false]);
  tp.push(['setApplePayMerchantId', process.env.APPLE_MERCHANT_ID]);
  // tp.push(['setDebug', process.env.PIANO_SANDBOX]);

  const composerCountryCode = await getComposerCountryCode();
  if (composerCountryCode) {
    tp.push(['setCustomVariable', 'countryCode', composerCountryCode]);
  }

  if (!process.env.PIANO_SANDBOX && process.env.PIANO_AID === 'cfQj2fM3zj') {
    tp.push(['setPianoIdUrl', process.env.PIANO_ID_URL]);
    tp.push(['setComposerHost', process.env.PIANO_COMPOSER_HOST]);
    tp.push(['setEndpoint', process.env.PIANO_ENDPOINT]);
    tp.push(['setStaticDomain', process.env.PIANO_STATIC_DOMAIN]);
  }

  tp.push(['setLinkedTermSelected', onLinkedTermSelected]);
  tp.push(['setRequestUserAuthForLinkedTerm', false]);

  const handlers = pianoHandlers(tp, updateResponseVariables, sendEventOnConsent, setConversionData, optimizely);
  (Object.keys(handlers) as Array<keyof typeof handlers>).forEach((name) => {
    tp.push(['addHandler', name, handlers[name]]);
  });

  return tp;
}

export function pianoSetPageData(
  tp: Tinypass,
  { contentSection, tags, publishDate }: PianoPageConfig,
  isBypassed = false,
) {
  const section = isBypassed ? 'Editorial Bypass' : contentSection;
  tp.push(['setContentSection', section]);
  tp.push(['setContentCreated', publishDate]);
  tp.push(['setPageURL', window.location.href]);
  tp.push(['setTags', tags]);
  // Postlaunch TODO:
  // tp.push(['setCustomVariable', 'contentType', channel]); // might be useful for bucketing page types

  return tp;
}

export function pianoMyAccount() {
  window.tp.myaccount.show({
    containerSelector: '.my-account-payment',
    displayMode: 'inline',
  });
}
